<template>
    <div :class="$styleCrit.container">
        <div :class="$styleCrit.grid">
            <div :class="$styleCrit.overlay_blur" />
            <!-- Should be on top for better SEO -->
            <LazyHydration :hydrate="hydrateOnIdle()">
                <div :class="$styleCrit.left">
                    <LeftSide :languageLinks="props.localeData?.languageLinks" />
                </div>
            </LazyHydration>
            <div :class="$styleCrit.right">
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <DesktopHeader :class="$styleCrit.desktop_header_override" />
                </LazyHydration>
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <MobileHeader :class="$styleCrit.mobile_header_override" />
                </LazyHydration>
                <slot />
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <Footer
                        :languageLinks="props.localeData?.languageLinks"
                        :additionalCategories="props.additionalCategories"
                    />
                </LazyHydration>
            </div>
            <StickyBanner />
        </div>

        <LazyHydration :hydrate="hydrateOnIdle()">
            <Toaster :isFullScreen="fullScreen" />
        </LazyHydration>
    </div>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, hydrateOnIdle, onMounted, onServerPrefetch, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import Footer from '@/components/Footer/Footer.vue'
import DesktopHeader from '@/components/Headers/DesktopHeader.vue'
import MobileHeader from '@/components/Headers/MobileHeader.vue'
import LazyHydration from '@/components/LazyHydration.vue'
import LeftSide from '@/components/LeftSide/LeftSide.vue'
import StickyBanner from '@/components/StickyBanner/StickyBanner.vue'
import Toaster from '@/components/Toaster/Toaster.vue'
import { useBodyScrollLock } from '@/composables/use-body-scroll-lock'
import { useUserInfoLoaded } from '@/composables/use-user-state'
import type { AvailableLocale } from '@/i18n/helpers'
import { useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useGoogleAuthStore } from '@/store/google-auth-store'
import { useLayoutStore } from '@/store/layout-store'
import { usePushSubscriptionStore } from '@/store/push-subscribe/store'
import { usePWAInstallStore } from '@/store/pwa-install/store'
import { useUserStore } from '@/store/user-store/user-store'
import type { LinkedCategory } from '@/types'

const props = defineProps<{
    localeData?: {
        languageLinks: Record<AvailableLocale, string>
    }
    additionalCategories?: LinkedCategory[]
}>()

const googleAuthStore = useGoogleAuthStore()
const layoutStore = useLayoutStore()
const userStore = useUserStore()
const appStore = useAppStore()

const { initialized } = storeToRefs(googleAuthStore)
const { isAuthorized } = storeToRefs(userStore)
const { fullScreen } = storeToRefs(layoutStore)
const userInfoLoaded = useUserInfoLoaded()

const { suspense } = useGeneralData()
const pwaStore = usePWAInstallStore()
const pushSubscriptionStore = usePushSubscriptionStore()
const isGamePage = computed(() => $route.name === ROUTE_NAMES.GAME_PAGE)
const $route = useRoute()
const toastTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const TOAST_DELAY_MS = 3000

function setPrompt() {
    if (initialized.value && userInfoLoaded.value) {
        googleAuthStore.setPrompt()
    }
}

function togglePrompt() {
    if (fullScreen.value) {
        googleAuthStore.cancelPrompt()
    } else {
        // set timeout fo authorize to prevent very fast cancel() calling if we have one than more notification
        setTimeout(() => {
            setPrompt()
        }, 100)
    }
}

watch([initialized, userInfoLoaded], setPrompt)
watch(fullScreen, togglePrompt)

useBodyScrollLock(fullScreen)

watch(isAuthorized, (newValue) => {
    if (newValue) {
        googleAuthStore.cancelPrompt()
    }
})

onServerPrefetch(suspense)
onMounted(() => {
    togglePrompt()
    if (!isGamePage.value || appStore.platform !== 'desktop') {
        toastTimeout.value = setTimeout(() => {
            toastTimeout.value = null
            if (appStore.isPWA) {
                pushSubscriptionStore.showSubscriptionPrompt({
                    considerMinVisits: true,
                })
            } else {
                pwaStore.showPWAInstallPrompt()
            }
        }, TOAST_DELAY_MS)
    }

    window.openLoginPopupFromAdBanner = () => {
        if (!isAuthorized.value && userInfoLoaded.value) {
            layoutStore.openSignInModal('window.open_login_popup_call')
        }
    }
})
onUnmounted(() => {
    if (toastTimeout.value) {
        clearTimeout(toastTimeout.value)
        toastTimeout.value = null
    }
})
</script>
<style module="$styleCrit" critical>
.desktop_header_override,
.mobile_header_override,
.left {
    opacity: 0;
}

:global(body) {
    /* show scroll bar before all the content arrive */
    /* this will be unset once non-critical style arrive (with the content) */
    min-height: 110vh;
}

:global(html.disable_scroll),
:global(html:has(dialog[open])) {
    overflow: hidden;
    position: relative;
    height: 100%;
    overscroll-behavior: none;
}

:global(html.disable_scroll body),
:global(html:has(dialog[open]) body) {
    overflow: hidden;
    position: relative;
    height: 100%;
    background: var(--carbon-100);
}

@media (--mobile-landscape) {
    :global(html.disable_scroll body),
    :global(html:has(dialog[open]) body) {
        height: 100dvh;
    }
}

@media (--pwa) {
    :global(html.disable_scroll),
    :global(html:has(dialog[open])),
    :global(html.disable_scroll body),
    :global(html:has(dialog[open]) body) {
        height: 100dvh;
    }
}

.container {
    min-height: 100%;
    background: var(--carbon-100);
    --global-padding-horizontal: 6px;
    --global-wide-scroller-padding-left: 12px;
    --global-wide-scroller-padding-right: 12px;
    --global-header-pannel-height: 52px;
    padding: 0 calc(var(--global-padding-horizontal) + var(--pg-safe-area-right)) 0
        calc(var(--global-padding-horizontal) + var(--pg-safe-area-left));
}

.grid {
    display: grid;
    gap: 6px;
    grid-template-columns: minmax(0, 1fr);
}

.desktop_header_override {
    height: 56px;
    margin: calc(12px + var(--pg-safe-area-top)) 0 16px;
    display: none;
    position: relative;
}

.mobile_header_override {
    display: flex;
    height: 66px;
    margin-top: var(--pg-safe-area-top);
}

.left {
    display: none;
    grid-column: 1;
    grid-row: 1;
    z-index: 1;
    max-height: 100vh;
    position: sticky;
    top: 0;
}

.right {
    grid-column: 1;
}

@media (--desktop-small-plus) {
    .container {
        --global-wide-scroller-padding-left: 0px;
    }

    .grid {
        /* left column is 56px until screen width reaches 1400px, then it is starts growing proportionally */
        grid-template-columns: 56px minmax(0, 1fr);
    }

    .left {
        display: block;
    }

    .right {
        grid-column: 2;
    }

    .desktop_header_override {
        display: flex;
    }

    .mobile_header_override {
        display: none;
    }
}

.overlay_blur {
    display: none;
}
</style>

<style module>
.desktop_header_override,
.mobile_header_override,
.left {
    opacity: 1;
}

:global(body) {
    min-height: unset;
}

.overlay_blur {
    position: fixed;
    width: 100vw;
    height: 100vh;
    left: 0;
    top: 0;
    z-index: 1;
    backdrop-filter: blur(5rem) brightness(0.15);
}

:global(html:has(*[data-overlay-blur])) .desktop_header_override {
    z-index: 1;
}

:global(html:has(*[data-overlay-blur])) .overlay_blur {
    display: block;
}
</style>
